import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    ProjectFieldServiceProxy,
    ProjectFieldConfigDto,
    SaveProjectFieldConfigInput,
    SaveSectionInput,
    SaveFieldInput,
    ProjectFieldSettingsDto,
    ProjectFieldType,
    NativeFieldKey,
    SaveProjectFieldValuesInput,
    UpdateProjectFieldValueInput,
    ProjectFieldValueInput,
    BuildNamePreviewInput,
    NativeValuePreviewInput,
    NextProjectNumberPreviewDto,
    ProjectNameMode,
    ProjectDetailsViewDto,
    GetProjectFieldValuesInput,
    ProjectFieldValuesRowDto,
} from '@shared/service-proxies/service-proxies';

/** A single field value to persist for a project (custom or native). */
export interface CfValuePayload {
    fieldDefinitionId: number;
    valueJson: string;
}

/** A native field display value used to build the project name preview. */
export interface CfNativePreviewValue {
    nativeKey: NativeFieldKey;
    displayValue: string;
}

/** A single field as edited in the Custom Fields editor working copy. */
export interface CfFieldVM {
    id?: number;
    label: string;
    fieldType: ProjectFieldType;
    required: boolean;
    isNative: boolean;
    isLocked: boolean;
    nativeKey: NativeFieldKey;
    settings?: ProjectFieldSettingsDto;
    // derived UI flags
    renameable: boolean;
    hasGear: boolean;
}

/** A section (with its ordered fields) in the editor working copy. */
export interface CfSectionVM {
    id?: number;
    name: string;
    isGeneral: boolean;
    collapsed: boolean;
    fields: CfFieldVM[];
}

/** One selectable custom field type for the "Create custom field" dropdown. */
export interface CfTypeOption {
    type: ProjectFieldType;
    label: string;
}

/**
 * Thin wrapper over ProjectFieldServiceProxy plus the enum→display maps and the
 * working-copy/save-input helpers. Shared by the editor and (later) the Details
 * tab, wizard and task slide-over so type labels/icons never drift.
 */
@Injectable({ providedIn: 'root' })
export class ProjectCustomFieldsService {
    constructor(private _proxy: ProjectFieldServiceProxy) {}

    getConfig(): Observable<ProjectFieldConfigDto> {
        return this._proxy.getFieldConfiguration().pipe(map(r => r.responseData));
    }

    saveConfig(input: SaveProjectFieldConfigInput): Observable<boolean> {
        return this._proxy.saveFieldConfiguration(input).pipe(map(r => !r.isError));
    }

    /**
     * Persists native-extra columns (project number/value/location) and custom field
     * values for a freshly-created project. The backend routes each value to a native
     * column or a TrProjectFieldValues row by its field-definition id.
     */
    saveProjectFieldValues(projectId: number, values: CfValuePayload[]): Observable<boolean> {
        const input = new SaveProjectFieldValuesInput();
        input.projectId = projectId;
        input.values = (values || []).map(v => {
            const item = new ProjectFieldValueInput();
            item.fieldDefinitionId = v.fieldDefinitionId;
            item.valueJson = v.valueJson;
            return item;
        });
        return this._proxy.saveProjectFieldValues(input).pipe(map(r => !r.isError));
    }

    /** Loads a project's saved field values (native + custom) for the Details surface. */
    getProjectDetails(projectId: number): Observable<ProjectDetailsViewDto> {
        return this._proxy.getProjectDetails(projectId).pipe(map(r => r.responseData));
    }

    /** Batch custom + native-extra field values for the given projects (project-list grid columns). */
    getProjectFieldValuesForProjects(projectIds: number[]): Observable<ProjectFieldValuesRowDto[]> {
        const input = new GetProjectFieldValuesInput();
        input.projectIds = projectIds;
        return this._proxy.getProjectFieldValuesForProjects(input).pipe(map(r => r.responseData || []));
    }

    /** Inline-updates a single field value (native column or custom JSON) on the Details surface. */
    updateProjectFieldValue(projectId: number, fieldDefinitionId: number, valueJson: string): Observable<boolean> {
        const input = new UpdateProjectFieldValueInput();
        input.projectId = projectId;
        input.fieldDefinitionId = fieldDefinitionId;
        input.valueJson = valueJson;
        return this._proxy.updateProjectFieldValue(input).pipe(map(r => !r.isError));
    }

    /** Reserves and assigns the next auto project number; resolves to the formatted number. */
    assignAutoProjectNumber(projectId: number): Observable<string> {
        return this._proxy.assignAutoProjectNumber(projectId)
            .pipe(map(r => (r && !r.isError ? r.responseData : undefined)));
    }

    /** Previews the next auto project number without reserving it. */
    getNextProjectNumberPreview(): Observable<NextProjectNumberPreviewDto> {
        return this._proxy.getNextProjectNumberPreview().pipe(map(r => r.responseData));
    }

    /** Builds the read-only project-name preview from the current field values. */
    buildProjectNamePreview(
        mode: ProjectNameMode,
        values: CfValuePayload[],
        nativeValues: CfNativePreviewValue[]
    ): Observable<string> {
        const input = new BuildNamePreviewInput();
        input.mode = mode;
        input.values = (values || []).map(v => {
            const item = new ProjectFieldValueInput();
            item.fieldDefinitionId = v.fieldDefinitionId;
            item.valueJson = v.valueJson;
            return item;
        });
        input.nativeValues = (nativeValues || []).map(n => {
            const item = new NativeValuePreviewInput();
            item.nativeKey = n.nativeKey;
            item.displayValue = n.displayValue;
            return item;
        });
        return this._proxy.buildProjectNamePreview(input).pipe(map(r => r.responseData));
    }

    /** True when rich-text HTML has no visible content — e.g. tiptap's empty-document
     *  output ('<p></p>', '<p><br></p>'). Such values must be treated as empty everywhere:
     *  rendered via innerHTML they produce a zero-height, unclickable element, which hides
     *  the placeholder AND the click-to-edit target. Media/embed tags count as content
     *  even though they contribute no text. */
    isRichTextEmpty(html: string): boolean {
        let text = '';
        if (html === undefined || html === null) {
            return true;
        }
        text = String(html);
        if (/<(img|iframe|video|audio|table|hr|embed|object)\b/i.test(text)) {
            return false;
        }
        text = text.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ');
        return text.trim() === '';
    }

    /** Lowercase type pill text shown next to a field (e.g. "rich text"). */
    typePill(type: ProjectFieldType): string {
        switch (type) {
            case ProjectFieldType._1: return 'text';
            case ProjectFieldType._2: return 'dropdown';
            case ProjectFieldType._3: return 'date';
            case ProjectFieldType._4: return 'client';
            case ProjectFieldType._5: return 'url';
            case ProjectFieldType._6: return 'currency';
            case ProjectFieldType._7: return 'address';
            case ProjectFieldType._8: return 'rich text';
            case ProjectFieldType._9: return 'date-range';
            case ProjectFieldType._10: return 'toggle';
            case ProjectFieldType._11: return 'location list';
            default: return '';
        }
    }

    /** Lucide icon name for a field type (used by value-surface field rows). */
    typeIcon(type: ProjectFieldType): string {
        switch (type) {
            case ProjectFieldType._1: return 'type';
            case ProjectFieldType._2: return 'list';
            case ProjectFieldType._3: return 'calendar';
            case ProjectFieldType._4: return 'user';
            case ProjectFieldType._5: return 'link';
            case ProjectFieldType._6: return 'dollar-sign';
            case ProjectFieldType._7: return 'map-pin';
            case ProjectFieldType._8: return 'align-left';
            case ProjectFieldType._9: return 'calendar-range';
            case ProjectFieldType._10: return 'toggle-left';
            case ProjectFieldType._11: return 'map-pin';
            default: return 'type';
        }
    }

    /** The custom field types offered in the "Create custom field" dropdown. */
    customFieldTypes(): CfTypeOption[] {
        return [
            { type: ProjectFieldType._1, label: 'Text' },
            { type: ProjectFieldType._2, label: 'Dropdown' },
            { type: ProjectFieldType._3, label: 'Date' },
            { type: ProjectFieldType._4, label: 'Client' },
            { type: ProjectFieldType._5, label: 'Url' },
            { type: ProjectFieldType._6, label: 'Currency' },
            { type: ProjectFieldType._7, label: 'Address' },
            { type: ProjectFieldType._8, label: 'Rich Text' },
            { type: ProjectFieldType._11, label: 'Location list' },
        ];
    }

    /** Parses a Location-list field's settings.options (one JSON entry per location). */
    parseLocationItems(settings: any): any[] {
        let out: any[] = [];
        ((settings && settings.options) || []).forEach((o: string) => {
            let item: any = null;
            if (!o) {
                return;
            }
            try {
                item = JSON.parse(o);
            } catch (e) {
                item = null;
            }
            if (item && typeof item === 'object') {
                out.push(item);
            } else {
                // Tolerate a plain/scalar entry (legacy data) — keep it as a display string.
                out.push({ name: '', formatted: o, url: '' });
            }
        });
        return out;
    }

    /** Single-line display for a location item, e.g. "Home - 123 St, Miami, FL http://x.com". */
    locationItemDisplay(item: any): string {
        let parts: string[] = [];
        if (!item) {
            return '';
        }
        if (item.name) {
            parts.push(item.name + (item.formatted ? ' - ' : ''));
        }
        if (item.formatted) {
            parts.push(item.formatted);
        }
        let text = parts.join('');
        if (item.url) {
            text = text ? text + ' ' + item.url : item.url;
        }
        return text;
    }

    /** The dropdown option strings a Location-list field offers on the value surfaces. */
    locationDisplayOptions(settings: any): string[] {
        return this.parseLocationItems(settings).map(i => this.locationItemDisplay(i)).filter(s => !!s);
    }

    /** Trailing URL of a location-list display string (the URL is always appended last). */
    locationDisplayUrl(display: any): string {
        let m: RegExpMatchArray = null;
        if (!display || typeof display !== 'string') {
            return '';
        }
        m = display.match(/(https?:\/\/\S+|www\.\S+)$/i);
        return m ? m[0] : '';
    }

    /** A location-list display string without its trailing URL. */
    locationDisplayText(display: any): string {
        let url = this.locationDisplayUrl(display);
        if (!display) {
            return '';
        }
        if (!url) {
            return display;
        }
        return String(display).substring(0, String(display).length - url.length).trim();
    }

    /** Native fields that may be relabeled (custom fields are always renameable). */
    isNativeRenameable(key: NativeFieldKey): boolean {
        switch (key) {
            case NativeFieldKey._1: // Project name
            case NativeFieldKey._2: // Project number
            case NativeFieldKey._6: // Project value
            case NativeFieldKey._7: // Project location
            case NativeFieldKey._8: // Description
                return true;
            default:
                return false; // Client, Stage, Duration, Privacy
        }
    }

    /** Privacy has no settings gear; every other field does. */
    hasGear(isNative: boolean, key: NativeFieldKey): boolean {
        if (isNative && key === NativeFieldKey._9) {
            return false; // Privacy
        }
        return true;
    }

    /** Builds the desired-state save payload from the editor's ordered sections. */
    buildSaveInput(sections: CfSectionVM[]): SaveProjectFieldConfigInput {
        const input = new SaveProjectFieldConfigInput();
        input.sections = sections.map((s, sIdx) => {
            const si = new SaveSectionInput();
            si.id = s.id;
            si.name = s.name;
            si.orderNumber = sIdx;
            si.isGeneral = s.isGeneral;
            si.fields = (s.fields || []).map((f, fIdx) => {
                const fi = new SaveFieldInput();
                fi.id = f.id;
                fi.label = f.label;
                fi.fieldType = f.fieldType;
                fi.orderNumber = fIdx;
                fi.isRequired = f.required;
                fi.isNative = f.isNative;
                fi.isLocked = f.isLocked;
                fi.nativeKey = f.nativeKey;
                fi.settings = f.settings;
                return fi;
            });
            return si;
        });
        return input;
    }
}
